import React, { useState } from 'react';

const ThreatIntelligence = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const tips = [
    {
      id: 1,
      category: 'best-practices',
      title: 'Enable Multi-Factor Authentication',
      description: 'Add an extra layer of security by requiring a second form of verification beyond just a password.',
      priority: 'high',
      icon: '🔐',
      actions: ['Enable MFA for all users', 'Use authenticator apps instead of SMS', 'Require MFA for admin accounts']
    },
    {
      id: 2,
      category: 'threat-detection',
      title: 'Monitor Unusual Login Patterns',
      description: 'Set up alerts for logins from new locations, unusual times, or multiple failed attempts.',
      priority: 'high',
      icon: '👁️',
      actions: ['Configure geolocation alerts', 'Set up failed login notifications', 'Review login history weekly']
    },
    {
      id: 3,
      category: 'best-practices',
      title: 'Regular Security Audits',
      description: 'Conduct periodic reviews of your security policies, access controls, and threat logs.',
      priority: 'medium',
      icon: '📋',
      actions: ['Schedule monthly security reviews', 'Audit user permissions quarterly', 'Review and update policies']
    },
    {
      id: 4,
      category: 'threat-detection',
      title: 'Implement Zero-Trust Architecture',
      description: 'Never trust, always verify. Authenticate and authorize every access request regardless of source.',
      priority: 'high',
      icon: '🛡️',
      actions: ['Verify all access requests', 'Use least privilege access', 'Segment your network']
    },
    {
      id: 5,
      category: 'incident-response',
      title: 'Create an Incident Response Plan',
      description: 'Prepare a documented plan for responding to security incidents quickly and effectively.',
      priority: 'high',
      icon: '🚨',
      actions: ['Document response procedures', 'Assign incident response roles', 'Conduct tabletop exercises']
    },
    {
      id: 6,
      category: 'best-practices',
      title: 'Keep Software Updated',
      description: 'Regularly update all systems, applications, and security tools to patch vulnerabilities.',
      priority: 'medium',
      icon: '⬆️',
      actions: ['Enable automatic updates', 'Track patch management', 'Test updates in staging first']
    },
    {
      id: 7,
      category: 'threat-detection',
      title: 'Monitor Network Traffic',
      description: 'Analyze network traffic patterns to detect anomalies and potential security breaches.',
      priority: 'medium',
      icon: '📡',
      actions: ['Set up traffic baselines', 'Configure anomaly alerts', 'Use network monitoring tools']
    },
    {
      id: 8,
      category: 'incident-response',
      title: 'Backup Critical Data',
      description: 'Maintain regular backups of critical data to ensure quick recovery from ransomware or data loss.',
      priority: 'high',
      icon: '💾',
      actions: ['Automate daily backups', 'Test restore procedures', 'Store backups offline']
    },
    {
      id: 9,
      category: 'best-practices',
      title: 'User Security Training',
      description: 'Educate employees about phishing, social engineering, and security best practices.',
      priority: 'medium',
      icon: '🎓',
      actions: ['Conduct security awareness training', 'Run phishing simulations', 'Create security guidelines']
    },
    {
      id: 10,
      category: 'threat-detection',
      title: 'Deploy Endpoint Detection',
      description: 'Use endpoint detection and response (EDR) tools to identify and respond to threats on devices.',
      priority: 'high',
      icon: '💻',
      actions: ['Install EDR on all endpoints', 'Configure real-time monitoring', 'Set up automated responses']
    },
  ];

  const filteredTips = selectedCategory === 'all' 
    ? tips 
    : tips.filter(tip => tip.category === selectedCategory);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'low': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <div className="min-h-screen bg-cyber-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <span className="text-4xl">🤖</span>
            <h1 className="text-3xl font-bold text-white">AI Security Assistant</h1>
          </div>
          <p className="text-gray-400">Intelligent security recommendations and best practices</p>
        </div>

        <div className="bg-cyber-card border border-gray-800 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">Filter by Category</h3>
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'all', label: 'All Tips' },
              { value: 'best-practices', label: 'Best Practices' },
              { value: 'threat-detection', label: 'Threat Detection' },
              { value: 'incident-response', label: 'Incident Response' },
            ].map((category) => (
              <button
                key={category.value}
                onClick={() => setSelectedCategory(category.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === category.value
                    ? 'bg-cyber-cyan text-cyber-darker'
                    : 'bg-cyber-darker text-gray-300 hover:bg-gray-700'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredTips.map((tip) => (
            <div
              key={tip.id}
              className="bg-cyber-card border border-gray-800 rounded-lg p-6 hover:border-cyber-cyan transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">{tip.icon}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{tip.title}</h3>
                    <span className={`inline-block px-2 py-1 text-xs rounded border mt-1 ${getPriorityColor(tip.priority)}`}>
                      {tip.priority} priority
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-gray-300 mb-4">{tip.description}</p>

              <div className="bg-cyber-darker border border-gray-700 rounded-lg p-4">
                <h4 className="text-sm font-medium text-cyber-cyan mb-2">Recommended Actions:</h4>
                <ul className="space-y-2">
                  {tip.actions.map((action, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <svg className="w-4 h-4 text-cyber-cyan mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm text-gray-300">{action}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center">
                <span className="text-2xl">⚡</span>
              </div>
              <div>
                <h3 className="text-sm text-gray-400">High Priority</h3>
                <p className="text-2xl font-bold text-white">
                  {tips.filter(t => t.priority === 'high').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-cyber-card border border-gray-800 rounded-lg p-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <span className="text-2xl">📊</span>
              </div>
              <div>
                <h3 className="text-sm text-gray-400">Medium Priority</h3>
                <p className="text-2xl font-bold text-white">
                  {tips.filter(t => t.priority === 'medium').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-cyber-card border border-gray-800 rounded-lg p-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-cyber-cyan/20 rounded-lg flex items-center justify-center">
                <span className="text-2xl">💡</span>
              </div>
              <div>
                <h3 className="text-sm text-gray-400">Total Tips</h3>
                <p className="text-2xl font-bold text-white">{tips.length}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThreatIntelligence;